import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HelpGuide from './HelpGuide';
import BatteryHealthModal from './BatteryHealthModal';

const API_BASE = 'https://api.camel.optitrac.com';

interface FleetVehicle {
  unitId: string; name: string; licensePlate: string; platform: 'Camel' | 'Starlink';
  lastTransmission: string; online: boolean; hoursAgo: number;
  vin?: string; vbat?: number; css?: number; spdk?: number;
  warning?: { lowVin?: number; eid9?: boolean };
  alertId?: string; notes?: any[]; reason?: string;
}

interface Props {
  onCreateTicket: (vehicle: { unitId: string; name: string; platform: string; issue: string }) => void;
}

const ClientFleetDashboard: React.FC<Props> = ({ onCreateTicket }) => {
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState<FleetVehicle | null>(null);
  const [vehicleHistory, setVehicleHistory] = useState<any[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'alerts' | 'online'>('all');
  const [search, setSearch] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [nextRefresh, setNextRefresh] = useState<Date | null>(null);
  const [supportHours, setSupportHours] = useState<any>(null);
  const [batteryModalVehicle, setBatteryModalVehicle] = useState<FleetVehicle | null>(null);

  const getHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('clientToken');
    return { Authorization: `Bearer ${token}` };
  };

  const formatTime = (iso: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const [refreshing, setRefreshing] = useState(false);

  const fetchFleet = async () => {
    setRefreshing(true);
    try {
      const headers = getHeaders();
      // Get client's devices to know which hex_values (unitIds) belong to them
      const devRes = await axios.get(`${API_BASE}/api/devices/my-devices`, { headers });
      const myDevices = devRes.data || [];
      const myHexValues = new Set(myDevices.map((d: any) => d.hex_value?.toUpperCase()).filter(Boolean));
      if (myHexValues.size === 0) { setVehicles([]); setLoading(false); return; }

      // Fetch Camel + Starlink vehicles
      const [camelRes, starlinkRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/camel/vehicles`, { headers }),
        axios.get(`${API_BASE}/api/starlink/vehicles?page=1&limit=500`, { headers })
      ]);

      const now = Date.now();
      const fleet: FleetVehicle[] = [];

      if (camelRes.status === 'fulfilled') {
        (camelRes.value.data || []).forEach((v: any) => {
          if (!myHexValues.has(v.unitId?.toUpperCase())) return;
          const diff = v.lastTransmission ? now - new Date(v.lastTransmission).getTime() : 0;
          fleet.push({
            unitId: v.unitId, name: v.name || v.licensePlate, licensePlate: v.licensePlate,
            platform: 'Camel', lastTransmission: v.lastTransmission || '', online: !!v.online,
            hoursAgo: diff ? Math.round(diff / 3600000) : 0,
            vin: v.vin, vbat: v.batteryVoltage, css: v.css, spdk: v.speedKmh,
            reason: (v.eid === 9 || v.eid === '9') ? 'Battery Disconnected (EID 9)' : undefined
          });
        });
      }

      if (starlinkRes.status === 'fulfilled') {
        const stData = starlinkRes.value.data;
        const stVehicles = stData?.vehicles || stData || [];
        stVehicles.forEach((v: any) => {
          if (!myHexValues.has(v.unitId?.toUpperCase())) return;
          const lt = v.lastTransmission || '';
          const isOnline = v.online === 'Active' || v.online === 'Parked';
          const diff = lt ? now - new Date(lt).getTime() : 0;
          fleet.push({
            unitId: v.unitId, name: v.name || v.licensePlate || v.unitId, licensePlate: v.licensePlate || v.name || '',
            platform: 'Starlink', lastTransmission: lt, online: isOnline,
            hoursAgo: lt ? Math.round(diff / 3600000) : -1
          });
        });
      }

      // Fetch warnings for all unitIds
      const unitIds = fleet.map(v => v.unitId);
      const camelIds = fleet.filter(v => v.platform === 'Camel').map(v => v.unitId);
      const starlinkIds = fleet.filter(v => v.platform === 'Starlink').map(v => v.unitId);
      const allWarnings: Record<string, { lowVin?: number; eid9?: boolean }> = {};

      await Promise.all([
        camelIds.length > 0 ? axios.post(`${API_BASE}/api/camel/warnings`, { unitIds: camelIds }, { headers }).then(r => Object.assign(allWarnings, r.data)).catch(() => {}) : null,
        starlinkIds.length > 0 ? axios.post(`${API_BASE}/api/starlink/warnings`, { unitIds: starlinkIds }, { headers }).then(r => Object.assign(allWarnings, r.data)).catch(() => {}) : null
      ]);

      // Fetch active alerts for notes/investigation status
      let activeAlerts: any[] = [];
      try {
        const ar = await axios.get(`${API_BASE}/api/alerts/active`, { headers });
        activeAlerts = ar.data || [];
      } catch {}
      const alertMap = new Map(activeAlerts.map((a: any) => [a.unit_id, a]));

      fleet.forEach(v => {
        const w = allWarnings[v.unitId];
        if (w) {
          v.warning = w;
          if (w.eid9) v.reason = 'Battery Disconnected (EID 9)';
          else if (w.lowVin && w.lowVin <= 10.10) v.reason = `Low Voltage (${w.lowVin.toFixed(2)}V)`;
        }
        const alert = alertMap.get(v.unitId);
        if (alert) {
          v.alertId = alert.id;
          v.notes = Array.isArray(alert.notes) ? alert.notes : [];
        }
      });

      fleet.sort((a, b) => {
        // Alerts first (offline or warnings), then online
        const aAlert = !a.online || a.warning;
        const bAlert = !b.online || b.warning;
        if (aAlert && !bAlert) return -1;
        if (!aAlert && bAlert) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });

      setVehicles(fleet);
    } catch (e) { console.error('Error fetching fleet:', e); }
    finally { setLoading(false); setRefreshing(false); setLastRefresh(new Date()); setNextRefresh(new Date(Date.now() + 5 * 60 * 1000)); }
  };

  const openVehicleHistory = async (unitId: string) => {
    setHistoryLoading(true);
    setVehicleHistory([]);
    try {
      const r = await axios.get(`${API_BASE}/api/alerts/vehicle/${unitId}`, { headers: getHeaders() });
      setVehicleHistory(r.data || []);
    } catch { setVehicleHistory([]); }
    finally { setHistoryLoading(false); }
  };

  useEffect(() => { fetchFleet(); const i = setInterval(fetchFleet, 5 * 60 * 1000); return () => clearInterval(i); }, []);

  useEffect(() => {
    const fetchSupportHours = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('clientUser') || localStorage.getItem('user') || '{}');
        const clientId = user.client_id;
        if (!clientId) return;

        const now = new Date();
        const monthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        
        const [clientRes, usageRes] = await Promise.all([
          axios.get(`${API_BASE}/api/clients/${clientId}`, { headers: getHeaders() }),
          axios.get(`${API_BASE}/api/time-tracking/clients/${clientId}/usage/${monthYear}`, { headers: getHeaders() })
        ]);

        setSupportHours({
          allocated: clientRes.data.monthly_hours_allocated || 0,
          used: usageRes.data.support_hours_used || 0,
          outside: usageRes.data.outside_support_cost || 0
        });
      } catch (error) {
        console.error('Error fetching support hours:', error);
      }
    };
    fetchSupportHours();
  }, []);


  const filtered = vehicles.filter(v => {
    if (filter === 'alerts' && v.online && !v.warning) return false;
    if (filter === 'online' && !v.online) return false;
    if (search) {
      const s = search.toLowerCase();
      return (v.name || '').toLowerCase().includes(s) || (v.unitId || '').toLowerCase().includes(s) || (v.licensePlate || '').toLowerCase().includes(s);
    }
    return true;
  });

  const totalVehicles = vehicles.length;
  const onlineCount = vehicles.filter(v => v.online && !v.warning).length;
  const alertCount = vehicles.filter(v => !v.online || v.warning).length;
  const batteryCount = vehicles.filter(v => v.warning).length;
  const investigatedCount = vehicles.filter(v => v.notes && v.notes.length > 0).length;

  const [generatingReport, setGeneratingReport] = useState(false);

  const generateBatteryReport = async () => {
    setGeneratingReport(true);
    try {
      const headers = getHeaders();
      const camelIds = vehicles.filter(v => v.platform === 'Camel').map(v => v.unitId);
      const starlinkIds = vehicles.filter(v => v.platform === 'Starlink').map(v => v.unitId);

      const [camelRes, starlinkRes] = await Promise.allSettled([
        camelIds.length > 0 ? axios.post(`${API_BASE}/api/camel/battery-report`, { unitIds: camelIds }, { headers }) : Promise.resolve({ data: [] }),
        starlinkIds.length > 0 ? axios.post(`${API_BASE}/api/starlink/battery-report`, { unitIds: starlinkIds }, { headers }) : Promise.resolve({ data: [] })
      ]);

      const allReports = [
        ...(camelRes.status === 'fulfilled' ? camelRes.value.data : []),
        ...(starlinkRes.status === 'fulfilled' ? starlinkRes.value.data : [])
      ];

      // Map unitId to vehicle_id (device name) from client's devices
      const devRes = await axios.get(`${API_BASE}/api/devices/my-devices`, { headers });
      const deviceNameMap = new Map((devRes.data || []).map((d: any) => [d.hex_value?.toUpperCase(), d.vehicle_id]));
      allReports.forEach((r: any) => {
        const deviceName = deviceNameMap.get(r.unitId?.toUpperCase());
        if (deviceName) r.vehicleName = deviceName;
      });

      if (allReports.length === 0) { alert('No vehicle data available for report.'); return; }

      const doc = new jsPDF('p', 'mm', 'a4');
      const pw = 210, ph = 297, mx = 15;
      const cw = pw - mx * 2;
      const now = new Date().toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

      const fmtTs = (ts: string | null) => {
        if (!ts) return 'N/A';
        try { return new Date(ts).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
        catch { return ts; }
      };

      type RGB = [number, number, number];
      const vinHealth = (vin: number | null, isEid9: boolean): { text: string; color: RGB } => {
        if (isEid9) return { text: 'BATTERY DISCONNECTED', color: [107, 114, 128] };
        if (vin === null) return { text: 'NO DATA', color: [107, 114, 128] };
        if (vin > 12) return { text: 'HEALTHY', color: [22, 163, 74] };
        if (vin > 10.1) return { text: 'LOW', color: [234, 88, 12] };
        return { text: 'CRITICAL', color: [220, 38, 38] };
      };

      const vbatHealth = (vbat: number | null): { text: string; color: RGB } => {
        if (vbat === null) return { text: 'NO DATA', color: [107, 114, 128] };
        if (vbat >= 3.91) return { text: 'OK', color: [22, 163, 74] };
        return { text: 'LOW', color: [220, 38, 38] };
      };

      const critical = allReports.filter((r: any) => !r.isEid9 && r.currentVin !== null && r.currentVin <= 10.1);
      const low = allReports.filter((r: any) => !r.isEid9 && r.currentVin !== null && r.currentVin > 10.1 && r.currentVin <= 12);
      const healthy = allReports.filter((r: any) => !r.isEid9 && (r.currentVin === null || r.currentVin > 12));
      const disconnected = allReports.filter((r: any) => r.isEid9);

      const drawHeader = (d: jsPDF) => {
        d.setFillColor(37, 99, 235);
        d.rect(0, 0, pw, 32, 'F');
        d.setFillColor(124, 58, 237);
        d.rect(pw / 2, 0, pw / 2, 32, 'F');
        d.setTextColor(255, 255, 255);
        d.setFontSize(18); d.setFont('helvetica', 'bold');
        d.text('OPTITRAC', mx, 14);
        d.setFontSize(10); d.setFont('helvetica', 'normal');
        d.text('Battery Health Report', mx, 22);
        d.setFontSize(8);
        d.text(`Generated: ${now}`, pw - mx, 22, { align: 'right' });
        d.setTextColor(0, 0, 0);
      };

      drawHeader(doc);
      let y = 42;

      doc.setFontSize(14); doc.setFont('helvetica', 'bold');
      doc.text('Fleet Battery Summary', mx, y); y += 10;

      const kpis = [
        { label: 'Total', value: allReports.length.toString(), color: [37, 99, 235] },
        { label: 'Critical', value: critical.length.toString(), color: [220, 38, 38] },
        { label: 'Low', value: low.length.toString(), color: [234, 88, 12] },
        { label: 'Healthy', value: healthy.length.toString(), color: [22, 163, 74] },
        { label: 'Disconnected', value: disconnected.length.toString(), color: [107, 114, 128] },
      ];
      const kpiW = cw / kpis.length;
      kpis.forEach((k, i) => {
        const kx = mx + i * kpiW;
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(kx + 1, y, kpiW - 2, 22, 3, 3, 'F');
        doc.setFontSize(16); doc.setFont('helvetica', 'bold');
        doc.setTextColor(k.color[0], k.color[1], k.color[2]);
        doc.text(k.value, kx + kpiW / 2, y + 10, { align: 'center' });
        doc.setFontSize(7); doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(k.label, kx + kpiW / 2, y + 18, { align: 'center' });
      });
      doc.setTextColor(0, 0, 0); y += 30;

      const tableRows = [...critical, ...low, ...disconnected]
        .map((r: any) => {
          const vh = vinHealth(r.currentVin, r.isEid9);
          const vb = vbatHealth(r.currentVbat);
          return [r.vehicleName || r.name || r.unitId, r.platform, r.currentVin !== null ? `${r.currentVin.toFixed(2)}V` : 'N/A', vh.text, r.currentVbat !== null ? `${r.currentVbat.toFixed(2)}V` : 'N/A', vb.text, fmtTs(r.lastTransmission)];
        });

      autoTable(doc, {
        startY: y,
        head: [['Vehicle', 'Platform', 'Vehicle Bat.', 'Status', 'Device Bat.', 'Status', 'Last Report']],
        body: tableRows,
        margin: { left: mx, right: mx },
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        didParseCell: (data: any) => {
          if (data.section === 'body') {
            if (data.column.index === 3) {
              const v = data.cell.raw;
              data.cell.styles.fontStyle = 'bold';
              if (v === 'CRITICAL') data.cell.styles.textColor = [220, 38, 38];
              else if (v === 'LOW') data.cell.styles.textColor = [234, 88, 12];
              else if (v === 'HEALTHY') data.cell.styles.textColor = [22, 163, 74];
              else data.cell.styles.textColor = [107, 114, 128];
            }
            if (data.column.index === 5) {
              data.cell.styles.fontStyle = 'bold';
              if (data.cell.raw === 'OK') data.cell.styles.textColor = [22, 163, 74];
              else if (data.cell.raw === 'LOW') data.cell.styles.textColor = [220, 38, 38];
              else data.cell.styles.textColor = [107, 114, 128];
            }
          }
        }
      });

      // Detail pages for vehicles with issues
      const detailVehicles = [...critical, ...low, ...disconnected];
      for (const r of detailVehicles as any[]) {
        doc.addPage();
        drawHeader(doc);
        let vy = 42;

        doc.setFontSize(16); doc.setFont('helvetica', 'bold'); doc.setTextColor(31, 41, 55);
        doc.text(r.vehicleName || r.name || r.unitId, mx, vy);
        doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(107, 114, 128);
        doc.text(`Unit: ${r.unitId}  \u2022  Platform: ${r.platform}`, mx, vy + 6);
        vy += 16;

        const vh = vinHealth(r.currentVin, r.isEid9);
        doc.setFillColor(vh.color[0], vh.color[1], vh.color[2]);
        doc.roundedRect(mx, vy, cw, 12, 3, 3, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
        doc.text(r.isEid9 ? 'BATTERY DISCONNECTED (EID 9) \u2014 Likely in workshop, not critical' : `VEHICLE BATTERY: ${vh.text}`, mx + 5, vy + 8);
        vy += 20;

        doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
        doc.text('Battery Readings', mx, vy); vy += 8;

        // VIN box
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(mx, vy, cw / 2 - 3, 30, 3, 3, 'F');
        doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(107, 114, 128);
        doc.text('Vehicle Battery', mx + 5, vy + 7);
        doc.setFontSize(18); doc.setFont('helvetica', 'bold');
        doc.setTextColor(vh.color[0], vh.color[1], vh.color[2]);
        doc.text(r.currentVin !== null ? `${r.currentVin.toFixed(2)}V` : 'N/A', mx + 5, vy + 22);

        // VBAT box
        const vb = vbatHealth(r.currentVbat);
        const vbx = mx + cw / 2 + 3;
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(vbx, vy, cw / 2 - 3, 30, 3, 3, 'F');
        doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(107, 114, 128);
        doc.text('Device Battery (needs > 3.91V)', vbx + 5, vy + 7);
        doc.setFontSize(18); doc.setFont('helvetica', 'bold');
        doc.setTextColor(vb.color[0], vb.color[1], vb.color[2]);
        doc.text(r.currentVbat !== null ? `${r.currentVbat.toFixed(2)}V` : 'N/A', vbx + 5, vy + 22);
        vy += 38;

        doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
        doc.text('Last Known Good Voltage (> 12V)', mx, vy); vy += 7;
        doc.setFontSize(10); doc.setFont('helvetica', 'normal');
        if (r.lastGoodVin) {
          doc.setTextColor(22, 163, 74);
          doc.text(`${r.lastGoodVin.voltage.toFixed(2)}V  \u2014  ${fmtTs(r.lastGoodVin.timestamp)}`, mx, vy);
        } else {
          doc.setTextColor(220, 38, 38);
          doc.text('No reading above 12V found in recent history', mx, vy);
        }
        vy += 12;

        doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
        doc.text('Last Transmission', mx, vy); vy += 7;
        doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(55, 65, 81);
        doc.text(fmtTs(r.lastTransmission), mx, vy); vy += 14;

        // Map
        if (r.latitude && r.longitude) {
          doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
          doc.text('Last Known Location', mx, vy); vy += 4;
          try {
            const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${r.latitude},${r.longitude}&zoom=15&size=640x320&scale=2&maptype=hybrid&markers=color:red%7Csize:mid%7C${r.latitude},${r.longitude}&key=AIzaSyAJTKR898aljDfpXT_PuXIFPDnrwHA7EAw`;
            const resp = await fetch(mapUrl);
            if (resp.ok) {
              const blob = await resp.blob();
              const dataUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
              if (dataUrl) { doc.addImage(dataUrl, 'PNG', mx, vy, cw, cw * 0.5, undefined, 'FAST'); vy += cw * 0.5 + 4; }
            }
          } catch {}
          doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(107, 114, 128);
          doc.text(`Coordinates: ${r.latitude.toFixed(6)}, ${r.longitude.toFixed(6)}`, mx, vy);
        }

        doc.setFontSize(7); doc.setTextColor(180, 180, 180);
        doc.text('Optitrac Battery Health Report \u2014 Confidential', pw / 2, ph - 10, { align: 'center' });
      }

      doc.setPage(1);
      doc.setFontSize(7); doc.setTextColor(180, 180, 180);
      doc.text('Optitrac Battery Health Report \u2014 Confidential', pw / 2, ph - 10, { align: 'center' });

      doc.save(`Optitrac_Battery_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error('Error generating battery report:', error);
      alert('Error generating report. Please try again.');
    } finally { setGeneratingReport(false); }
  };

  const fmtDuration = (ms: number) => {
    if (ms < 60000) return '< 1m';
    const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Refresh bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', fontSize: '12px', color: '#9ca3af' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <span>🕐 Last refresh: <strong style={{ color: '#374151' }}>{lastRefresh ? lastRefresh.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</strong></span>
          <span>⏭ Next refresh: <strong style={{ color: '#374151' }}>{nextRefresh ? nextRefresh.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</strong></span>
        </div>
        <button onClick={() => fetchFleet()} disabled={refreshing} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: refreshing ? 'wait' : 'pointer', fontWeight: 600, fontSize: '13px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', opacity: refreshing ? 0.6 : 1 }}>
          {refreshing ? '⏳ Refreshing...' : '🔄 Refresh Now'}
        </button>
      </div>

      {/* Support Hours Widget */}
      {supportHours && (
        <div style={{
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          border: '2px solid #bae6fd',
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.15)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                backgroundColor: '#2563eb',
                borderRadius: '12px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '20px' }}>⏱️</span>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e40af' }}>
                  Support Hours - {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </h3>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#2563eb' }}>
                  {supportHours.used.toFixed(1)} / {supportHours.allocated}
                </div>
              </div>
            </div>

            <div style={{
              width: '100%',
              height: '12px',
              backgroundColor: 'white',
              borderRadius: '6px',
              overflow: 'hidden',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                width: `${Math.min((supportHours.used / supportHours.allocated) * 100, 100)}%`,
                height: '100%',
                background: (supportHours.used / supportHours.allocated) > 0.9 ? 'linear-gradient(90deg, #ef4444, #dc2626)' :
                           (supportHours.used / supportHours.allocated) > 0.75 ? 'linear-gradient(90deg, #f59e0b, #ea580c)' :
                           'linear-gradient(90deg, #2563eb, #1e40af)',
                transition: 'width 0.3s'
              }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ color: '#1e40af', fontWeight: '700' }}>
                {(supportHours.allocated - supportHours.used) >= 0 
                  ? `${(supportHours.allocated - supportHours.used).toFixed(1)}h remaining` 
                  : `${Math.abs(supportHours.allocated - supportHours.used).toFixed(1)}h over`}
              </span>
              {supportHours.outside > 0 && (
                <span style={{ color: '#ea580c', fontWeight: '700' }}>
                  Outside: ${supportHours.outside.toFixed(2)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total Fleet', value: totalVehicles, icon: '🚛', bg: '#eff6ff', color: '#2563eb' },
          { label: 'Online', value: onlineCount, icon: '🟢', bg: '#f0fdf4', color: '#16a34a' },
          { label: 'Alerts', value: alertCount, icon: '🔴', bg: '#fef2f2', color: '#dc2626' },
          { label: 'Battery Warnings', value: batteryCount, icon: '🔋', bg: '#fff7ed', color: '#ea580c' },
          { label: 'Being Investigated', value: investigatedCount, icon: '🔍', bg: '#f5f3ff', color: '#7c3aed' },
        ].map((t, i) => (
          <div key={i} style={{ background: t.bg, borderRadius: '12px', padding: '16px', textAlign: 'center', border: `1px solid ${t.color}20` }}>
            <div style={{ fontSize: '20px' }}>{t.icon}</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: t.color }}>{t.value}</div>
            <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{t.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        {(['all', 'alerts', 'online'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
            background: filter === f ? '#2563eb' : '#f3f4f6', color: filter === f ? '#fff' : '#555'
          }}>{f === 'all' ? `All (${totalVehicles})` : f === 'alerts' ? `Alerts (${alertCount})` : `Online (${onlineCount})`}</button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vehicles..."
          style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', width: '250px', marginLeft: 'auto' }} />
        <button onClick={generateBatteryReport} disabled={generatingReport || vehicles.length === 0}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: generatingReport ? 'wait' : 'pointer', fontWeight: 600, fontSize: '13px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', opacity: generatingReport ? 0.6 : 1 }}>
          {generatingReport ? '⏳ Generating...' : '🔋 Battery Report'}
        </button>
      </div>

      {loading ? <p style={{ textAlign: 'center', color: '#888', padding: '40px' }}>Loading fleet...</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {filtered.map(v => {
            const isAlert = !v.online || v.warning;
            const hasWarning = !!v.warning;
            const hasNotes = v.notes && v.notes.length > 0;
            const bg = hasWarning ? '#fff7ed' : !v.online ? '#eff6ff' : hasNotes ? '#f0fdf4' : '#fff';
            const border = hasWarning ? '#fb923c' : !v.online ? '#93c5fd' : hasNotes ? '#86efac' : '#e5e7eb';
            const shadow = hasWarning ? '0 0 12px rgba(251,146,60,0.3)' : !v.online ? '0 0 12px rgba(147,197,253,0.3)' : hasNotes ? '0 0 12px rgba(134,239,172,0.3)' : 'none';

            return (
              <div key={v.unitId} style={{ background: bg, borderRadius: '14px', padding: '18px', border: `1.5px solid ${border}`, boxShadow: shadow, cursor: 'pointer', transition: 'transform 0.15s' }}
                onClick={() => { setSelectedVehicle(v); openVehicleHistory(v.unitId); }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={e => (e.currentTarget.style.transform = '')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>{v.platform === 'Camel' ? '🐫' : '⭐'}</span>
                    <span style={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{v.name}</span>
                  </div>
                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', fontWeight: 600,
                    background: v.online && !hasWarning ? '#dcfce7' : '#fee2e2',
                    color: v.online && !hasWarning ? '#16a34a' : '#dc2626'
                  }}>{v.online && !hasWarning ? '● Online' : '● Offline'}</span>
                </div>

                {v.reason && (
                  <div style={{ background: '#fff1e6', borderRadius: '8px', padding: '6px 10px', marginBottom: '8px', fontSize: '12px', fontWeight: 600, color: '#ea580c' }}>
                    🔋 {v.reason}
                  </div>
                )}

                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  <div>Unit: {v.unitId} • {v.platform}</div>
                  {v.lastTransmission && <div>Last seen: {formatTime(v.lastTransmission)}{!v.online && v.hoursAgo > 0 ? ` (${v.hoursAgo}h ago)` : ''}</div>}
                </div>

                {v.platform === 'Camel' && (v.vbat !== undefined || v.css !== undefined) && (
                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px', marginBottom: '8px' }}>
                    {v.vbat !== undefined && <span style={{ padding: '2px 6px', borderRadius: '4px', background: v.vbat < 12 ? '#fee2e2' : '#f3f4f6', color: v.vbat < 12 ? '#dc2626' : '#555' }}>VBAT: {v.vbat}V</span>}
                    {v.css !== undefined && <span style={{ padding: '2px 6px', borderRadius: '4px', background: v.css < 10 ? '#fee2e2' : '#f3f4f6', color: v.css < 10 ? '#dc2626' : '#555' }}>CSS: {v.css}</span>}
                    {v.spdk !== undefined && <span style={{ padding: '2px 6px', borderRadius: '4px', background: '#f3f4f6', color: '#555' }}>SPD: {v.spdk}</span>}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {hasNotes && <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>🔍 Being investigated • 📝 {v.notes!.length} notes</span>}
                  {!hasNotes && isAlert && !hasWarning && <span style={{ fontSize: '11px', color: '#2563eb', fontWeight: 600 }}>🔍 Optitrac Investigation Underway</span>}
                  {!hasNotes && isAlert && hasWarning && <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>⚠️ Battery Issue</span>}
                  {!isAlert && !hasNotes && <span style={{ fontSize: '11px', color: '#16a34a' }}>✅ All good</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Vehicle Detail Modal */}
      {selectedVehicle && (() => {
        const v = selectedVehicle;
        const isAlert = !v.online || v.warning;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setSelectedVehicle(null); setVehicleHistory(null); }}>
            <div style={{ background: '#fff', borderRadius: '16px', width: '650px', maxHeight: '85vh', overflow: 'auto', padding: '28px' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px' }}>{v.platform === 'Camel' ? '🐫' : '⭐'} {v.name}</h2>
                  <span style={{ color: '#888', fontSize: '13px' }}>Unit: {v.unitId} • {v.platform} • {v.online ? 'Online' : 'Offline'}</span>
                </div>
                <button onClick={() => { setSelectedVehicle(null); setVehicleHistory(null); }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#999' }}>✕</button>
              </div>

              {/* Battery warning banner */}
              {v.warning && (
                <div style={{ background: '#fff7ed', border: '1px solid #fb923c', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>🔋</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#ea580c', fontSize: '14px' }}>Battery Issue Detected</div>
                    <div style={{ fontSize: '12px', color: '#9a3412' }}>
                      {v.warning.eid9 && <span>⚠️ EID 9 — Battery disconnected. </span>}
                      {v.warning.lowVin !== undefined && v.warning.lowVin <= 10.10 && <span>⚠️ Low voltage: {v.warning.lowVin.toFixed(2)}V</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Investigation notes (read-only) */}
              {v.notes && v.notes.length > 0 && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '13px', marginBottom: '8px' }}>🔍 Being Investigated — {v.notes.length} notes</div>
                  <div style={{ maxHeight: '120px', overflow: 'auto' }}>
                    {v.notes.map((n: any, i: number) => (
                      <div key={i} style={{ padding: '4px 0', borderTop: i > 0 ? '1px solid #dcfce7' : 'none', fontSize: '12px' }}>
                        <span style={{ color: '#888' }}>{n.user} • {formatTime(n.timestamp)}</span>: <span style={{ color: '#333' }}>{n.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Incident History KPIs */}
              {vehicleHistory !== null && !historyLoading && vehicleHistory.length > 0 && (() => {
                const items = vehicleHistory;
                const resolved = items.filter((i: any) => i.status === 'resolved');
                const totalDowntime = resolved.reduce((sum: number, i: any) => {
                  if (i.created_at && i.resolved_at) return sum + (new Date(i.resolved_at).getTime() - new Date(i.created_at).getTime());
                  return sum;
                }, 0);
                return (
                  <>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '10px' }}>📊 Incident History</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
                      {[
                        { label: 'Total Incidents', value: items.length, icon: '🚨', bg: '#fef2f2', color: '#dc2626' },
                        { label: 'Resolved', value: resolved.length, icon: '✅', bg: '#f0fdf4', color: '#16a34a' },
                        { label: 'Total Downtime', value: fmtDuration(totalDowntime), icon: '⏱️', bg: '#eff6ff', color: '#2563eb' },
                      ].map((t, i) => (
                        <div key={i} style={{ background: t.bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                          <div style={{ fontSize: '18px' }}>{t.icon}</div>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: t.color }}>{t.value}</div>
                          <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{t.label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                      {items.slice(0, 5).map((item: any, idx: number) => {
                        const dur = item.created_at && item.resolved_at ? new Date(item.resolved_at).getTime() - new Date(item.created_at).getTime() : 0;
                        const isActive = item.status === 'active';
                        return (
                          <div key={idx} style={{ background: isActive ? '#fef2f2' : '#fafafa', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${isActive ? '#fca5a5' : '#eee'}`, fontSize: '12px' }}>
                            <span style={{ fontWeight: 600, color: isActive ? '#dc2626' : '#333' }}>
                              {isActive ? '🔴 Active' : '✅ Resolved'} — {formatTime(item.created_at)}
                            </span>
                            {item.reason && item.reason !== 'Offline' && <span style={{ marginLeft: '8px', background: '#fff7ed', color: '#ea580c', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>🔋 {item.reason}</span>}
                            {(!item.reason || item.reason === 'Offline') && <span style={{ marginLeft: '8px', background: '#f0f4ff', color: '#2563eb', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>📡 Offline</span>}
                            {!isActive && <span style={{ float: 'right', color: '#888' }}>{fmtDuration(dur)}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
              {historyLoading && <p style={{ textAlign: 'center', color: '#888', fontSize: '13px' }}>Loading history...</p>}

              {/* Log Ticket Button */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setBatteryModalVehicle(v)} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>
                  🔋 Battery Health
                </button>
                <button onClick={() => {
                  const issue = v.reason || (!v.online ? `Offline for ${v.hoursAgo}h` : 'General enquiry');
                  onCreateTicket({ unitId: v.unitId, name: v.name, platform: v.platform, issue });
                  setSelectedVehicle(null);
                  setVehicleHistory(null);
                }} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>
                  🎫 Log Ticket
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      <HelpGuide />
      
      {/* Battery Health Modal */}
      <BatteryHealthModal
        isOpen={!!batteryModalVehicle}
        onClose={() => setBatteryModalVehicle(null)}
        unitId={batteryModalVehicle?.unitId || ''}
        vehicleName={batteryModalVehicle?.name || ''}
        platform={batteryModalVehicle?.platform?.toLowerCase() as 'camel' | 'starlink'}
      />
    </div>
  );
};

export default ClientFleetDashboard;
